import { nanoid } from "@reduxjs/toolkit";
import { AmbientLight, Scene, Vector3 } from "three";
import { t } from "../../../localization";
import { clearViewerTooltip, setViewerTooltip } from "../../viewer";
import { CustomMouseEvent, OrbitPosition, PanoControls } from "../controls";
import { textToColor } from "../labelling";
import { Viewer } from "../main";
import { RayCaster } from "../ray-caster";
import { getPointScale } from "../utilities";
import { ClippingBox, PositionHandle, RotateHandle, ScaleHandle } from "./clipping-box";

export enum OperationType {
  Delete,
  Classify,
  Split
}

class ClippingGroup {
  public type;
  public name;
  public color;
  public id = nanoid();
  public active = false;
  public newClassification = 0;
  public ignoredClassification = 0;

  private markupList: PointMarkupList;
  public clippingBoxes: ClippingBox[] = [];

  constructor(markupList: PointMarkupList, type, name) {
    this.markupList = markupList;
    this.type = type;
    this.setName(name);
  }

  get isDelete() {
    return this.type === OperationType.Delete;
  }

  get isClassify() {
    return this.type === OperationType.Classify;
  }

  get isSplit() {
    return this.type === OperationType.Split;
  }

  get controls() {
    return this.markupList.controls;
  }

  get scene() {
    return this.markupList.scene;
  }

  getByID(id) {
    return this.clippingBoxes.find(x => x.id === id);
  }

  add(clippingBox: ClippingBox) {
    clippingBox.setColor(this.color);
    clippingBox.hidePoints = this.isDelete;
    clippingBox.updateClassification = this.isClassify;
    clippingBox.newClassification = this.newClassification;
    clippingBox.ignoredClassification = this.ignoredClassification;

    this.clippingBoxes.push(clippingBox);
    this.scene.add(clippingBox);
    this.setActive(false);
  }

  setName(name) {
    this.name = name;
    this.color = textToColor(name);

    this.clippingBoxes.forEach(clippingBox => {
      clippingBox.setColor(this.color);
    });
  }

  setNewClassification(value) {
    this.newClassification = value;

    this.clippingBoxes.forEach(clippingBox => {
      clippingBox.newClassification = value;
    });
  }

  setIgnoredClassification(value) {
    this.ignoredClassification = value;

    this.clippingBoxes.forEach(clippingBox => {
      clippingBox.ignoredClassification = value;
    });
  }

  setActive(state) {
    this.active = state;
  }

  setHover(id, hover) {
    const clippingBox = this.getByID(id);
    if (!clippingBox) return;

    clippingBox.setHover(hover);
  }

  zoom(id) {
    const clippingBox = this.getByID(id);
    if (!clippingBox) return;

    const pivot = clippingBox.position;

    const axisOffset = new Vector3(2, 0, 0.25)
      .applyQuaternion(clippingBox.quaternion)
      .multiply(clippingBox.scale)
      .multiplyScalar(2.0);

    const orbit = new Vector3()
      .copy(clippingBox.position)
      .add(axisOffset);

    const position = {orbit, pivot, smooth: true} as OrbitPosition;
    this.controls.setOrbitValues(true, position);
  }

  resetRotation(id) {
    const clippingBox = this.getByID(id);
    if (!clippingBox) return;

    let count = 0;
    let totalCount = 20;

    let dx = clippingBox.rotation.x / totalCount;
    let dy = clippingBox.rotation.y / totalCount;
    let dz = clippingBox.rotation.z / totalCount;

    const interval = setInterval(() => {
      const index = totalCount - count;

      clippingBox.rotation.set(
        dx*index,
        dy*index,
        dz*index
      );

      if (count === totalCount) {
        clearInterval(interval);
      }

      count = count + 1;
    }, 16);
  }

  removeByID(id) {
    const clippingBox = this.getByID(id);
    if (!clippingBox) return;

    this.scene.remove(clippingBox);
    this.clippingBoxes = this.clippingBoxes.filter(x => x.id !== id);
  }

  clear() {
    this.clippingBoxes.forEach(clippingBox => {
      this.scene.remove(clippingBox);
    });

    this.clippingBoxes = [];
  }

  update() {
    this.clippingBoxes.forEach(clippingBox => {
      clippingBox.update();
    });
  }
}

export class PointMarkupList {
    public scene: Scene;
    public controls: PanoControls
    private setMarkupGroups;

    public enabled = false;
    private mouseEvent = null;
    private shiftDown = false;
    private dragObject = null;

    public clippingGroups: ClippingGroup[] = [];

    constructor(controls: PanoControls, props) {
      const {setMarkupGroups} = props;

      this.controls = controls;
      this.setMarkupGroups = setMarkupGroups;

      this.initScene();
    }

    get viewer(): Viewer {
      return this.controls.viewer;
    }

    get scaleTooltip() {
      return [
        t('point-markup.click_and_hold_to_adjust_bounding_box'),
        t('point-markup.hold_shift_to_select_background_planes')
      ];
    }

    get rotateTooltip() {
      return [
        t('point-markup.click_and_hold_to_rotate_bounding_box')
      ];
    }

    get moveTooltip() {
      return [
        t('point-markup.click_and_hold_to_move_bounding_box')
      ];
    }

    get standardTooltip() {
      return [
        t('point-markup.click_to_add_new_bounding_box'),
        t('point-markup.right-click_to_cancel')
      ];
    }

    get active() {
      return !!this.activeGroup;
    }

    get activeGroup() {
      return this.clippingGroups.find(x => x.active);
    }

    get clippingBoxes() {
      return this.clippingGroups.map(x => x.clippingBoxes).flat();
    }

    initScene() {
      this.scene = new Scene();
      this.scene.add(new AmbientLight(0xFFFFFF));
    }

    getByID(id) {
      return this.clippingGroups.find(x => x.id === id);
    }

    setName(id, name) {
      const clippingGroup = this.getByID(id);
      if (!clippingGroup) return;

      clippingGroup.setName(name);
      this.refreshMarkupList();
    }

    setActive(id) {
      this.clippingGroups.forEach(group => {
        group.setActive(group.id === id);
      });
      this.refreshMarkupList();
    }

    addNewGroup(type, name) {
      let newGroup = new ClippingGroup(this, type, name);
      this.clippingGroups.push(newGroup);
      this.refreshMarkupList();
    }

    refreshMarkupList() {
      this.setMarkupGroups([...this.clippingGroups]);
    }

    setState(state) {
      if (state) {
        this.enable();
      } else {
        this.disable();
      }
    }

    zoom(id) {
      this.clippingGroups.forEach(group => {
        group.zoom(id);
      });
    }

    resetRotation(id) {
      this.clippingGroups.forEach(group => {
        group.resetRotation(id);
      });
    }

    removeClippingGroup(id) {
      const clippingGroup = this.getByID(id);
      if (!clippingGroup) return;

      clippingGroup.clippingBoxes.forEach(clippingBox => {
        this.scene.remove(clippingBox);
      });

      this.clippingGroups = this.clippingGroups.filter(x => x.id !== id);
      this.refreshMarkupList();
    }

    removeClippingBox(id) {
      this.clippingGroups.forEach(clippingGroup => {
        clippingGroup.removeByID(id);
      });

      this.refreshMarkupList();
    }

    setHover(id, state) {
      this.clippingGroups.forEach(clippingGroup => {
        clippingGroup.setHover(id, state);
      });
    }

    setNewClassification(id, value) {
      const clippingGroup = this.getByID(id);
      if (!clippingGroup) return;

      clippingGroup.setNewClassification(value);
    }

    setIgnoredClassification(id, value) {
      const clippingGroup = this.getByID(id);
      if (!clippingGroup) return;

      clippingGroup.setIgnoredClassification(value);
    }

    enable() {
      this.enabled = true;
    }

    disable() {
      this.enabled = false;

      this.clippingGroups.forEach(clippingGroup => {
        clippingGroup.clear();
      });

      this.clippingGroups = [];
      this.refreshMarkupList();

      clearViewerTooltip();
    }

    clear() {
      this.disable();
      this.enable();
    }

    addNewBlock(event) {
      if (this.dragObject) return;

      let {coordinate} = this.controls.getPickerResults(event);
      if (!coordinate) return;

      const camera = this.viewer.camera;

      const clippingBox = new ClippingBox(this.viewer);
      clippingBox.position.copy(coordinate);

      const vector = new Vector3()
        .add(coordinate)
        .sub(camera.position)
        .setLength(1.0);

      const cameraAngle = Math.atan2(vector.y, vector.x) - Math.PI/2;
      const newBoxScale = getPointScale(camera, clippingBox);
      clippingBox.rotation.z = cameraAngle;
      clippingBox.scale.setScalar(newBoxScale);

      this.activeGroup.add(clippingBox);

      clearViewerTooltip();
      this.refreshMarkupList();
    }

    clearNewBlock() {
      this.clippingGroups.forEach(group => {
        group.setActive(false);
      });

      clearViewerTooltip();
      this.refreshMarkupList();
    }

    setDragObject(dragObject) {
      this.dragObject = dragObject;

      let draggable = dragObject ? true : false;
      let controls = this.viewer.controls;
      controls.setMovementState(!draggable);
    }

    getDragObject(event, camera) {
      const raycaster = new RayCaster(this.viewer, event);

      this.clippingBoxes.forEach(object => {
        object.setVisible(false);
      });

      // Ignore clipping box if we are inside its bounds
      const objects = this.clippingBoxes.filter(clippingBox => {
        const bbox = clippingBox.boundingBox;
        return !bbox.containsPoint(camera.position);
      });

      let intersects = raycaster.intersectObjects(objects, {
        recursive: false
      });

      if (intersects.length === 0) {
        this.setDragObject(null);
        return;
      }

      // Position and rotation have priority
      const handleOverride = intersects
        .find(intersect => {
          const isPosition = intersect.object instanceof PositionHandle;
          const isRotation = intersect.object instanceof RotateHandle;
          return isPosition || isRotation;
        });

      if (handleOverride) {
        this.setDragObject(handleOverride);
      } else {
        if (this.shiftDown && intersects.length > 1) {
          this.setDragObject(intersects[1]);
        } else {
          this.setDragObject(intersects[0]);
        }
      }

      const {object} = this.dragObject;
      object.highlighted = true;
    }

    onMouseUp(event: CustomMouseEvent) {
      if (!this.enabled || !this.active || event.mouseMoved) {
        return false;
      }

      if (event.isLeftClick) {
        this.addNewBlock(event);
      } else if (event.isRightClick) {
        this.clearNewBlock();
      }

      return true;
    }

    onMouseMove(event: CustomMouseEvent) {
      if (!this.enabled || !event) return;

      const camera = this.viewer.camera;
      this.mouseEvent = event;

      if (this.dragObject) {
        const {object} = this.dragObject;
        if (object instanceof RotateHandle) {
          setViewerTooltip(this.rotateTooltip);
        } else if (object instanceof PositionHandle) {
          setViewerTooltip(this.moveTooltip);
        } else if (object instanceof ScaleHandle) {
          setViewerTooltip(this.scaleTooltip);
        }
      } else {
        if (this.active) {
          setViewerTooltip(this.standardTooltip);
        } else {
          clearViewerTooltip();
        }
      }

      if (event.mouseDown && event.isLeftClick) {
        if (this.dragObject) {
          const {object, point} = this.dragObject;
          object.drag(event, point);
          return true;
        } else {
          return false;
        }
      }

      this.getDragObject(event, camera);
      return false;
    }

    onKeyDown(event) {
      if (!this.enabled || this.shiftDown) return;
      if (event.key !== "Shift") return;

      this.shiftDown = true;
      this.onMouseMove(this.mouseEvent);
    }

    onKeyUp() {
      if (!this.enabled) return;

      this.shiftDown = false;
      this.setDragObject(null);
      this.onMouseMove(this.mouseEvent);
    }

    update() {
      this.clippingGroups.forEach(group => {
        group.update();
      });
    }
};
